/* eslint-disable no-param-reassign */
import { cloneObject } from '@/helpers/utility';
import { transferPersonnelToStation, getPersonnelId } from '../utils/personnelStationUtils';
import routeListService from '../../services/routeList';

/**
 * Build a Map<stationId, station> from a draft route's stations array, filtering to
 * stations that have a routeId (excludes orphan/legacy stations without one).
 */
function buildStationMap(draft) {
  return new Map((draft.stations || []).filter((s) => s.routeId).map((s) => [s.id, s]));
}

/**
 * Resolve the ordered list of forward stations from a draft route's path.lstRouteStopIds,
 * reversed to match the return direction's traversal order.
 */
function getReversedForwardStations(draft) {
  const { lstRouteStopIds } = draft?.draftVersion?.routeDetail?.path || {};
  if (!lstRouteStopIds?.length) return [];
  const stationMap = buildStationMap(draft);
  return lstRouteStopIds
    .map((id) => stationMap.get(id))
    .filter(Boolean)
    .reverse();
}

/**
 * Build the addStation API payload for a return-direction station that mirrors a forward
 * station. The counterStopId links the return station back to its forward counterpart.
 */
function buildReturnStationPayload(forwardStation, routeId) {
  const { description, location, id: forwardStationId } = forwardStation;
  const { latitude, longitude, address } = location || {};
  return {
    address,
    description,
    routeId,
    location: { latitude, longitude, address },
    counterStopId: forwardStationId,
  };
}

/**
 * Call addStation and normalize the result: attach isPassthrough and routeId onto the
 * returned station object (riders are attached later, in assembleStationSyncResults). Returns a uniform { success, ... } shape for both
 * success and failure so callers can handle them in a single loop.
 *
 * @param {Object} payload - addStation payload (from buildReturnStationPayload)
 * @param {Object} meta - { isPassthrough, routeId, forwardStationId, opType }
 * @returns {Promise<{ success: boolean, newStation?: Object, error?: string }>}
 */
function createOrUpdateReturnStation(payload, meta) {
  const { isPassthrough, routeId, forwardStationId, opType } = meta;
  return routeListService
    .addStation(payload)
    .then((newStation) => {
      newStation.isPassthrough = isPassthrough === true;
      newStation.routeId = routeId;
      return { success: true, newStation };
    })
    .catch((err) => {
      console.error(
        `Failed to ${opType} return station for forward station ${forwardStationId} on route ${routeId}`,
        err,
      );
      return { success: false, error: err.message || 'Unknown error' };
    });
}

/**
 * Collect { [personnelId]: stationId } mappings from a station's personnelIds and
 * personnels arrays. Used to build returnPathPersonnelStations for the draft update.
 */
function collectPersonnelStationMappings(station) {
  const mappings = {};
  const { personnelIds, personnels } = station || {};
  if (personnelIds?.length) {
    personnelIds.forEach((pid) => {
      mappings[pid] = station.id;
    });
  }
  if (personnels?.length) {
    personnels.forEach((p) => {
      const personnelId = getPersonnelId(p);
      if (personnelId != null) mappings[personnelId] = station.id;
    });
  }
  return mappings;
}

/**
 * Commit UPDATE_DRAFT_RETURN_PATH and ADD_MODIFIED_ROUTE for a route after its return
 * path has been rebuilt. Shared by clone and sync actions.
 *
 * By default, an empty `returnStopIds` is treated as "sync produced nothing" (e.g. every
 * API call failed) and is ignored so we don't clobber an existing return path with an
 * empty one. Pass `allowEmpty: true` when an empty result is a legitimate, deterministic
 * outcome (e.g. pruning orphaned return stations left no stations behind) so the return
 * path is actually cleared.
 */
function commitReturnPathUpdate(
  commit,
  { routeId, returnStopIds, returnPathPersonnelStations, stations, allowEmpty = false },
) {
  if (!returnStopIds.length && !allowEmpty) return;
  commit(
    'UPDATE_DRAFT_RETURN_PATH',
    {
      routeId,
      returnPath: { lstRouteStopIds: returnStopIds },
      returnPathPersonnelStations,
      stations,
    },
    { root: true },
  );
  commit('ADD_MODIFIED_ROUTE', routeId, { root: true });
}

/**
 * Commit UPDATE_PERSONNEL_STATION_ASSIGNMENT for each { personnelId, station } pair,
 * updating the instancePersonnel returnStation field for the FROM direction.
 */
function commitPersonnelAssignments(commit, { assignments, routeId }) {
  assignments.forEach(({ personnelId, station }) => {
    commit(
      'routePersonnel/UPDATE_PERSONNEL_STATION_ASSIGNMENT',
      {
        personnelId: parseInt(personnelId, 10),
        station: {
          id: station.id,
          name: station.description,
          description: station.description,
          location: station.location,
        },
        routeId,
        direction: 'FROM',
      },
      { root: true },
    );
  });
}

/**
 * Find the counter return station for a given forward station, trying multiple strategies:
 * 1. Direct: return station's counterStopId === forward station's current id
 * 2. Stale link: forward station was moved (got new id), return station's counterStopId
 *    still points to the old id — resolve via newToOldIds
 * 3. Reverse lookup: forward station's own counterStopId points to the return station
 * 4. Positional fallback: reversed-index i matches return station at index i
 *
 * @param {Object} params
 * @param {Object} params.forwardStation - the forward station being matched
 * @param {number} params.reversedIndex - index of the forward station in the reversed list
 * @param {Map} params.returnStationById - return stations keyed by id
 * @param {Map} params.returnStationByCounterStopId - return stations keyed by their counterStopId
 * @param {Array} params.returnStationsInOrder - return stations in path order
 * @param {Object} params.newToOldIds - { newForwardId: oldForwardId } from stationIdMappings
 * @param {Set} params.claimedReturnIds - return station ids already claimed by a previous match
 * @param {number} params.forwardStationsCount - total number of forward stations (for positional guard)
 * @returns {Object|null} the matched return station, or null if no match
 */
function findCounterReturnStation({
  forwardStation,
  reversedIndex,
  returnStationById,
  returnStationByCounterStopId,
  returnStationsInOrder,
  newToOldIds,
  claimedReturnIds,
  forwardStationsCount,
}) {
  const { id: forwardId } = forwardStation;

  // 1. Direct match
  let counterReturn = returnStationByCounterStopId.get(forwardId);

  // 2. Stale link — forward station was moved, return station's counterStopId still points to old id
  if (!counterReturn) {
    const oldId = newToOldIds[forwardId];
    if (oldId) {
      counterReturn = returnStationByCounterStopId.get(oldId);
    }
  }

  // 3. Reverse lookup — forward station's own counterStopId points to the return station
  if (!counterReturn && forwardStation.counterStopId) {
    counterReturn = returnStationById.get(forwardStation.counterStopId);
  }

  // 4. Positional fallback — reversed-index i corresponds to return station at index i.
  //    Catches pairs where counterStopId links are missing or stale; without it, unmatched
  //    stations would be recreated with new ids, defeating "only sync updated stations".
  //    Only safe when forward and return station counts match — if stations were added or
  //    deleted, positional indices shift and would produce incorrect pairings.
  if (!counterReturn && forwardStationsCount === returnStationsInOrder.length) {
    const positionalMatch = returnStationsInOrder[reversedIndex];
    if (positionalMatch && !claimedReturnIds.has(positionalMatch.id)) {
      counterReturn = positionalMatch;
    }
  }

  // Skip if already claimed by another forward station
  if (counterReturn && claimedReturnIds.has(counterReturn.id)) {
    return null;
  }

  return counterReturn;
}

/**
 * Inspect a draft route's return leg. A one-way route is represented with a "stub" return
 * path (null polyline/sections, zero distance/duration) rather than by omitting it, so that
 * shape must not be mistaken for a real second direction.
 *
 * @param {Object} routeDetail - draftVersion.routeDetail
 * @returns {{ isStub: boolean, stopIds: Array<number> }}
 */
function getReturnLegInfo(routeDetail) {
  const returnPath = routeDetail?.returnPath;
  if (!returnPath) return { isStub: false, stopIds: [] };

  const isStub =
    returnPath.encodedPolyline === null &&
    returnPath.sections === null &&
    returnPath.distanceInKm === 0 &&
    returnPath.durationInMin === 0;

  return { isStub, stopIds: isStub ? [] : returnPath.lstRouteStopIds || [] };
}

/**
 * Index return stations by their own id and by the forward station they shadow.
 */
function buildReturnStationLookups(returnStationsInOrder) {
  const returnStationById = new Map();
  const returnStationByCounterStopId = new Map();
  returnStationsInOrder.forEach((station) => {
    returnStationById.set(station.id, station);
    if (station.counterStopId) {
      returnStationByCounterStopId.set(station.counterStopId, station);
    }
  });
  return { returnStationById, returnStationByCounterStopId };
}

/**
 * Which leg a clicked stop belongs to: 'FROM' for a return-leg stop, 'TO' otherwise.
 *
 * A stop shared by both legs counts as forward; the return leg picks the rider up through
 * the counterpart merge when the user confirms applying changes to both directions.
 */
function resolveClickedLegDirection(draft, stationId) {
  const routeDetail = draft?.draftVersion?.routeDetail;
  const forwardStopIds = routeDetail?.path?.lstRouteStopIds || [];
  if (forwardStopIds.includes(stationId)) return 'TO';

  const { stopIds: returnStopIds } = getReturnLegInfo(routeDetail);
  return returnStopIds.includes(stationId) ? 'FROM' : 'TO';
}

/**
 * Map each rider assigned to a synced forward stop to the task whose return counterpart will
 * carry them. Only tasks that actually produce a counterpart count: 'reuse' tasks and
 * successful 'update'/'create' tasks. Riders of 'skip' or failed tasks keep whatever return
 * stop they already have, so a failed move never drops them from the return leg.
 *
 * Riders whose assignment was last edited on the return leg are left out entirely: mirroring
 * their forward stop would move them off the return stop the user just picked.
 *
 * @param {Object} [riderLegEdits] - { [personnelId]: 'TO' | 'FROM' } from modifiedRiderLegs
 */
function buildForwardRiderTasks(tasks, taskResults, riderLegEdits = {}) {
  const riderTasks = new Map();
  tasks.forEach((task) => {
    const producesCounterpart = task.type === 'reuse' || taskResults.get(task)?.success;
    if (!producesCounterpart) return;
    const { personnelIds = [], personnels = [] } = task.forwardStation || {};
    [...personnelIds, ...personnels.map(getPersonnelId)].forEach((personnelId) => {
      if (personnelId == null || riderLegEdits[personnelId] === 'FROM') return;
      riderTasks.set(personnelId, task);
    });
  });
  return riderTasks;
}

/**
 * Union a forward stop's riders into its return counterpart, so confirming "apply changes to
 * both directions" carries rider assignments across. Riders assigned directly on the return
 * leg are kept, because the return direction can be edited independently — unless the rider's
 * forward stop now mirrors onto a different counterpart (`forwardRiderTasks` resolves to
 * another task), in which case they move there instead of being left on both return stops.
 * With `includeForwardRiders: false` only the return station's own (still-owned) riders are
 * kept — used when an update failed and the old return station stays in the path.
 */
function mergeLegPersonnel(
  task,
  forwardRiderTasks = new Map(),
  { includeForwardRiders = true, riderLegEdits = {} } = {},
) {
  const { returnStation } = task;
  const forwardStation = includeForwardRiders ? task.forwardStation : null;
  const staysOnReturn = (personnelId) => {
    const ownerTask = forwardRiderTasks.get(personnelId);
    return !ownerTask || ownerTask === task;
  };
  // A rider last assigned on the return leg keeps that stop, so their forward stop must not
  // mirror them onto its own counterpart as well — that would place them on both return stops.
  const mirrorsFromForward = (personnelId) => personnelId != null && riderLegEdits[personnelId] !== 'FROM';

  const forwardPersonnelIds = (forwardStation?.personnelIds || []).filter(mirrorsFromForward);
  const forwardPersonnels = (forwardStation?.personnels || []).filter((p) => mirrorsFromForward(getPersonnelId(p)));

  const personnelIds = [
    ...new Set([...(returnStation?.personnelIds || []).filter(staysOnReturn), ...forwardPersonnelIds]),
  ];

  const personnels = [];
  const seen = new Set();
  const returnPersonnels = (returnStation?.personnels || []).filter((p) => staysOnReturn(getPersonnelId(p)));
  [...returnPersonnels, ...forwardPersonnels].forEach((p) => {
    const personnelId = getPersonnelId(p);
    if (personnelId == null || seen.has(personnelId)) return;
    seen.add(personnelId);
    personnels.push(p);
  });

  // Clone so the forward and return stations never share rider objects
  return { personnelIds, personnels: cloneObject(personnels) };
}

/**
 * Dedupe a station's riders into { personnelId, station } pairs for instancePersonnel
 * updates. A station's personnelIds and personnels arrays typically describe the same set.
 */
function collectPersonnelAssignments(station) {
  const assignments = [];
  const seen = new Set();

  (station.personnelIds || []).forEach((pid) => {
    if (seen.has(pid)) return;
    seen.add(pid);
    assignments.push({ personnelId: pid, station });
  });

  (station.personnels || []).forEach((p) => {
    const personnelId = getPersonnelId(p);
    if (personnelId == null || seen.has(personnelId)) return;
    seen.add(personnelId);
    assignments.push({ personnelId, station });
  });

  return assignments;
}

/**
 * Run the addStation API call for every 'update'/'create' task in parallel ('reuse' tasks
 * need no API call). Returns a Map of task → settled result, keyed by task object identity.
 *
 * @param {Array<Object>} tasks - classified tasks, each { type: 'reuse'|'update'|'create', forwardStation, returnStation? }
 * @param {number} routeId
 * @returns {Promise<Map>} task → { success, newStation? , error? }
 */
async function runStationSyncTasks(tasks, routeId) {
  const apiTasks = tasks.filter((t) => t.type === 'update' || t.type === 'create');

  const apiPromises = apiTasks.map((task) => {
    const { forwardStation } = task;
    const { id: forwardStationId, isPassthrough } = forwardStation;
    const payload = buildReturnStationPayload(forwardStation, routeId);

    return createOrUpdateReturnStation(payload, {
      isPassthrough,
      routeId,
      forwardStationId,
      opType: task.type,
    }).then((result) => ({ ...result, task }));
  });

  const apiResults = await Promise.allSettled(apiPromises);

  const taskResults = new Map();
  apiResults.forEach((settled) => {
    if (settled.status === 'fulfilled') {
      taskResults.set(settled.value.task, settled.value);
    }
  });

  return taskResults;
}

/**
 * Assemble the ordered return path from classified tasks and their API results:
 * - 'reuse' tasks contribute their existing return station as-is (no API call was made)
 * - 'update'/'create' tasks contribute the newly returned station on success, or fall back
 *   to the old return station (for 'update') / are dropped (for 'create') on failure
 *
 * Riders are merged here rather than before the API calls, so which counterpart a rider moves
 * to is decided from the actual outcomes (see buildForwardRiderTasks).
 *
 * @param {Array<Object>} tasks - classified tasks in return-path order
 * @param {Map} taskResults - task → settled API result, from runStationSyncTasks
 * @param {Object} [previousReturnMap] - the route's returnPathPersonnelStations before the sync;
 *   only riders whose return stop differs from it are reported in personnelAssignments
 * @returns {Object} { returnStopIds, newStations, returnPathPersonnelStations, personnelAssignments, reusedCount, errors }
 */
function assembleStationSyncResults(tasks, taskResults, previousReturnMap = {}, riderLegEdits = {}) {
  const returnStopIds = [];
  const newStations = [];
  const returnPathPersonnelStations = {};
  const personnelAssignments = []; // { personnelId, station }
  let reusedCount = 0;
  const errors = [];
  const forwardRiderTasks = buildForwardRiderTasks(tasks, taskResults, riderLegEdits);

  const addReturnStation = (station) => {
    returnStopIds.push(station.id);
    newStations.push(station);
    Object.assign(returnPathPersonnelStations, collectPersonnelStationMappings(station));
    // Riders already on this stop need no instancePersonnel update
    const changedAssignments = collectPersonnelAssignments(station).filter(
      ({ personnelId }) => String(previousReturnMap[personnelId]) !== String(station.id),
    );
    personnelAssignments.push(...changedAssignments);
  };

  tasks.forEach((task) => {
    if (task.type === 'reuse') {
      // The station itself is unchanged, so no API call was made — but riders newly assigned
      // to the forward stop still have to reach its counterpart, which is the whole point of
      // confirming "apply changes to both directions".
      addReturnStation({ ...task.returnStation, ...mergeLegPersonnel(task, forwardRiderTasks, { riderLegEdits }) });
      reusedCount += 1;
      return;
    }

    // SKIP: forward station has no return counterpart and was not moved — don't create one.
    // The return path simply won't include a station for this forward stop.
    if (task.type === 'skip') {
      return;
    }

    const result = taskResults.get(task);
    if (result?.success) {
      // Updates merge the forward stop's riders into the ones the return station already has;
      // creates have no return station, so they just take the forward stop's riders.
      addReturnStation({ ...result.newStation, ...mergeLegPersonnel(task, forwardRiderTasks, { riderLegEdits }) });
    } else {
      errors.push({
        stationId: task.forwardStation.id,
        error: result?.error || 'Unknown error',
      });
      // Fall back to the old return station if this was an update (best-effort recovery),
      // keeping the riders it already had unless they successfully moved elsewhere
      if (task.type === 'update' && task.returnStation) {
        addReturnStation({
          ...task.returnStation,
          ...mergeLegPersonnel(task, forwardRiderTasks, { includeForwardRiders: false, riderLegEdits }),
        });
      }
    }
  });

  return { returnStopIds, newStations, returnPathPersonnelStations, personnelAssignments, reusedCount, errors };
}

/**
 * Commit the assembled sync results (return path update + personnel assignments) and build
 * the uniform result object returned to callers of clone/sync actions.
 */
function finalizeStationSync(commit, routeId, assembled) {
  const { returnStopIds, newStations, returnPathPersonnelStations, personnelAssignments, reusedCount, errors } =
    assembled;

  if (returnStopIds.length) {
    commitReturnPathUpdate(commit, {
      routeId,
      returnStopIds,
      returnPathPersonnelStations,
      stations: newStations,
    });
    commitPersonnelAssignments(commit, { assignments: personnelAssignments, routeId });
  }

  return {
    syncedStations: newStations,
    returnStopIds,
    reusedCount,
    errors: errors.length > 0 ? errors : undefined,
    success: errors.length === 0,
    partial: errors.length > 0 && returnStopIds.length > 0,
  };
}

/**
 * Shared core for saveDrafts/activateDrafts: prepares draft data, persists it via the
 * appropriate service call, and updates store state accordingly. Activation additionally
 * snapshots/restores return paths (cleared by the backend on activate), tracks personnel
 * modifications, and refreshes route data afterwards.
 *
 * @param {Object} context - { commit, dispatch, rootState } from the calling action
 * @param {Object} params - { instanceId, draftIds, activate }
 * @returns {Promise<Object>} the service result (updated routes)
 */
async function persistDraftRoutes({ commit, dispatch, rootState }, { instanceId, draftIds, activate }) {
  commit('SET_IS_LOADING', true);
  try {
    const ids = activate ? [...new Set(draftIds)] : draftIds;

    // Snapshot return paths before activation (they get cleared by the backend)
    const returnPathSnapshot = {};
    if (activate) {
      ids.forEach((routeId) => {
        const draft = rootState.routeList.draftRoutes[routeId];
        const { returnPath, returnPathPersonnelStations } = draft?.draftVersion?.routeDetail ?? {};
        if (returnPath?.encodedPolyline) {
          returnPathSnapshot[routeId] = { returnPath, returnPathPersonnelStations };
        }
      });
    }

    const routeDrafts = await dispatch('prepareDraftData', ids);
    await dispatch('saveStationStats', routeDrafts);

    const result = activate
      ? await routeListService.activateDrafts(instanceId, { routeDrafts })
      : await routeListService.updateDrafts(instanceId, { routeDrafts });

    commit('SET_ALL_ROUTES', result, { root: true });

    if (activate && Object.keys(returnPathSnapshot).length > 0) {
      commit('RESTORE_RETURN_PATHS', returnPathSnapshot, { root: true });
    }

    const draftIdSet = new Set(ids.map((id) => Number(id)));
    commit(
      'SET_MODIFIED_ROUTES',
      rootState.routeList.modifiedRoutes.filter((id) => !draftIdSet.has(Number(id))),
      { root: true },
    );

    if (activate) {
      commit(
        'SET_MODIFIED_ROUTES_PERSONNEL',
        rootState.routeList.modifiedRoutesPersonnel.filter((id) => !draftIdSet.has(Number(id))),
        { root: true },
      );
    }

    // Scoped to the processed route ids only, so an unrelated unsaved route's pending
    // added/moved-station tracking isn't wiped out by this save/activate call.
    // The rider leg markers are dropped here too — this point is only reached once the drafts
    // are persisted, so a failed save leaves them intact for the retry.
    ids.forEach((id) => {
      commit('CLEAR_STATION_ID_MAPPINGS', id, { root: true });
      commit('CLEAR_MODIFIED_RIDER_LEGS', id, { root: true });
    });

    if (activate) {
      await dispatch('fetchRouteData', { instanceId });
    }

    return result;
  } catch (error) {
    console.error(`Failed to ${activate ? 'activate' : 'save'} drafts:`, error);
    throw error;
  } finally {
    commit('SET_IS_LOADING', false);
  }
}

/**
 * Sync a single route's return path: classify each reversed forward station as
 * reuse / update / create, run API calls for update+create, assemble the new return path,
 * and commit the results. Returns the result object for this route.
 *
 * @param {Object} params - { routeId, forwardStations, existingReturnStopIds, previousReturnMap, riderLegEdits, stationMap, stationIdMappings, newForwardStationIds, commit }
 * @returns {Promise<Object>} { syncedStations, returnStopIds, reusedCount, errors, success, partial }
 */
async function syncRouteReturnPath({
  routeId,
  forwardStations,
  existingReturnStopIds,
  previousReturnMap,
  riderLegEdits,
  stationMap,
  stationIdMappings,
  newForwardStationIds,
  commit,
}) {
  // Build return station lookup maps
  const returnStationsInOrder = existingReturnStopIds.map((id) => stationMap.get(id)).filter(Boolean);
  const { returnStationById, returnStationByCounterStopId } = buildReturnStationLookups(returnStationsInOrder);

  // Build reverse id mapping (newId → oldId) from stationIdMappings so we can resolve
  // stale counterStopId links on return stations after forward station moves.
  const newToOldIds = {};
  Object.entries(stationIdMappings).forEach(([oldId, newId]) => {
    newToOldIds[newId] = Number(oldId);
  });

  const reversedForward = [...forwardStations].reverse();
  const claimedReturnIds = new Set();

  // A forward station counts as "moved" iff its current id appears in newToOldIds (i.e.
  // stationIdMappings recorded an old→new id change for it, from a marker move) OR it's in
  // newForwardStationIds (i.e. it's genuinely new to this route's forward path, from
  // ADD_STATION_TO_ROUTE or assignStationToRoute, which never change a station's id so they
  // can't be tracked via stationIdMappings).
  const wasForwardMovedOrAdded = (forwardId) =>
    Object.prototype.hasOwnProperty.call(newToOldIds, forwardId) ||
    Object.prototype.hasOwnProperty.call(newForwardStationIds, forwardId);

  // Classify each forward station into: reuse, update, create, or skip.
  // We do NOT compare locations because forward and return locations can legitimately differ
  // when the user independently edited the return direction — using locMatch would overwrite
  // those independent edits.
  const tasks = reversedForward.map((forwardStation, reversedIndex) => {
    const { id: forwardId } = forwardStation;
    const counterReturn = findCounterReturnStation({
      forwardStation,
      reversedIndex,
      returnStationById,
      returnStationByCounterStopId,
      returnStationsInOrder,
      newToOldIds,
      claimedReturnIds,
      forwardStationsCount: reversedForward.length,
    });

    if (counterReturn) {
      claimedReturnIds.add(counterReturn.id);
      if (wasForwardMovedOrAdded(forwardId)) {
        return { type: 'update', forwardStation, returnStation: counterReturn };
      }
      return { type: 'reuse', forwardStation, returnStation: counterReturn };
    }

    // No counter found for this forward station. Previously this always created a new return
    // station, but that incorrectly created return counterparts for existing forward stations
    // that simply never had one (e.g. 3 forward stops, 1 return stop — moving 1 forward stop
    // would create 2 new return stops for the other 2, making both directions have 3 stops).
    // Now: only CREATE if the forward station was moved or newly added (its counter may have
    // been deleted, or it never had one yet). Unmoved, previously-existing forward stations
    // without a counter are SKIP'd — they never had a return counterpart and shouldn't get one
    // just because a different station was moved.
    if (wasForwardMovedOrAdded(forwardId)) {
      return { type: 'create', forwardStation };
    }

    return { type: 'skip', forwardStation };
  });

  // Process API calls in parallel for update/create tasks (reuse/skip need no API call), then
  // assemble the ordered return path and commit the results.
  const taskResults = await runStationSyncTasks(tasks, routeId);
  const assembled = assembleStationSyncResults(tasks, taskResults, previousReturnMap, riderLegEdits);
  return finalizeStationSync(commit, routeId, assembled);
}

/**
 * Handle the case where a route's forward direction has become completely empty (e.g. its
 * last remaining forward stop was just reassigned to a different route via
 * assignStationToRoute). With no forward stations left, syncRouteReturnPath has nothing to
 * mirror from, so it must not simply be skipped — any return-direction station that is a
 * mirror of a (now-gone) forward stop needs to be dropped, otherwise it lingers forever.
 *
 * A return station counts as a "mirror" iff it has a `counterStopId` (created to shadow a
 * forward stop). Standalone return-only stations (added directly to the return leg, with no
 * counterStopId) are left untouched.
 *
 * @param {Object} params - { routeId, existingReturnStopIds, stationMap, commit }
 * @returns {Object|null} the result object for this route, or null if nothing changed
 */
function pruneOrphanedReturnStations({ routeId, existingReturnStopIds, stationMap, commit }) {
  const returnStations = existingReturnStopIds.map((id) => stationMap.get(id)).filter(Boolean);
  const preservedStations = returnStations.filter((station) => !station.counterStopId);

  if (preservedStations.length === returnStations.length) {
    // Nothing to prune - all existing return stations are standalone
    return null;
  }

  const returnStopIds = preservedStations.map((station) => station.id);
  const returnPathPersonnelStations = {};
  preservedStations.forEach((station) => {
    Object.assign(returnPathPersonnelStations, collectPersonnelStationMappings(station));
  });

  commitReturnPathUpdate(commit, {
    routeId,
    returnStopIds,
    returnPathPersonnelStations,
    stations: [],
    allowEmpty: true,
  });

  return { syncedStations: [], returnStopIds, reusedCount: preservedStations.length, success: true };
}

/**
 * RoutesMap Store Module
 *
 * Manages map-specific operations for route editing including:
 * - Station reassignment between routes
 * - Drawing mode for optimization selection
 * - Map UI state management
 */

export default {
  namespaced: true,

  state: {
    // Loading state
    isLoading: false,

    // Selected date for route data fetching (null = current/latest)
    selectedDate: null,

    // Edit message banner visibility
    isCloseEditMessage: false,

    // Drawing mode state
    isDrawingMode: false,
    drawnPolygons: [],

    // UI state for side panels
    sidePanelState: {
      isVisible: false,
      mode: null, // 'add' | 'edit' | 'assign'
      stationData: null,
      routeId: null,
    },

    // Map bounds state
    fitBoundsCoordinates: null,
    shouldApplyFitBounds: false,

    // Temporary operation data
    pendingStationReassignment: null,
  },

  getters: {
    /**
     * Check if there are any pending operations
     */
    hasPendingOperations(state) {
      return state.pendingStationReassignment !== null;
    },

    /**
     * Get current side panel configuration
     */
    sidePanelConfig(state) {
      return state.sidePanelState;
    },

    /**
     * Check if drawing mode is active
     */
    isDrawingActive(state) {
      return state.isDrawingMode;
    },

    /**
     * Check if side panel is visible
     */
    isSidePanelVisible(state) {
      return state.sidePanelState.isVisible;
    },

    /**
     * Get current side panel mode
     */
    sidePanelMode(state) {
      return state.sidePanelState.mode;
    },

    /**
     * Get side panel station data
     */
    sidePanelStationData(state) {
      return state.sidePanelState.stationData;
    },

    /**
     * Get all drawn polygons
     */
    drawnPolygons(state) {
      return state.drawnPolygons;
    },

    /**
     * Get count of drawn polygons
     */
    drawnPolygonCount(state) {
      return state.drawnPolygons.length;
    },

    /**
     * Check if any polygons have been drawn
     */
    hasDrawnPolygons(state) {
      return state.drawnPolygons.length > 0;
    },

    /**
     * Get fit bounds coordinates
     */
    fitBoundsCoordinates(state) {
      return state.fitBoundsCoordinates;
    },

    /**
     * Check if fit bounds should be applied
     */
    shouldApplyFitBounds(state) {
      return state.shouldApplyFitBounds;
    },
  },

  mutations: {
    /**
     * Set drawing mode state
     */
    SET_DRAWING_MODE(state, isActive) {
      state.isDrawingMode = isActive;
    },

    /**
     * Add a drawn polygon to the collection
     */
    ADD_DRAWN_POLYGON(state, polygon) {
      state.drawnPolygons.push(polygon);
    },

    /**
     * Clear all drawn polygons
     */
    CLEAR_DRAWN_POLYGONS(state) {
      state.drawnPolygons = [];
    },

    /**
     * Set side panel state
     */
    SET_SIDE_PANEL_STATE(state, { isVisible, mode, stationData, routeId }) {
      state.sidePanelState = {
        isVisible: isVisible ?? state.sidePanelState.isVisible,
        mode: mode ?? state.sidePanelState.mode,
        stationData: stationData ?? state.sidePanelState.stationData,
        routeId: routeId ?? state.sidePanelState.routeId,
      };
    },

    /**
     * Reset side panel state
     */
    RESET_SIDE_PANEL_STATE(state) {
      state.sidePanelState = {
        isVisible: false,
        mode: null,
        stationData: null,
        routeId: null,
      };
    },

    /**
     * Set fit bounds coordinates
     */
    SET_FIT_BOUNDS_COORDINATES(state, coordinates) {
      state.fitBoundsCoordinates = coordinates;
    },

    /**
     * Set whether to apply fit bounds
     */
    SET_SHOULD_APPLY_FIT_BOUNDS(state, shouldApply) {
      state.shouldApplyFitBounds = shouldApply;
    },

    /**
     * Set pending station reassignment data
     */
    SET_PENDING_STATION_REASSIGNMENT(state, data) {
      state.pendingStationReassignment = data;
    },

    /**
     * Clear pending station reassignment
     */
    CLEAR_PENDING_STATION_REASSIGNMENT(state) {
      state.pendingStationReassignment = null;
    },

    SET_IS_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },

    SET_SELECTED_DATE(state, value) {
      state.selectedDate = value;
    },

    SET_IS_CLOSE_EDIT_MESSAGE(state, value) {
      state.isCloseEditMessage = value;
    },
  },

  actions: {
    /**
     * Assign a station from one route to another
     *
     * @param {Object} payload - Assignment payload
     * @param {Object} payload.originalStation - Original station object with routeId and id
     * @param {Number} payload.newRouteId - Target route ID
     * @param {Number} payload.stationOrder - Position in target route
     */
    async assignStationToRoute({ commit, rootState }, payload) {
      const { originalStation, newRouteId, stationOrder } = payload;
      const originalRouteId = originalStation.routeId;
      const originalStationId = originalStation.id;

      // Validate draft routes exist
      const originalRoute = rootState.routeList.draftRoutes[originalRouteId];
      const targetRoute = rootState.routeList.draftRoutes[newRouteId];

      if (!originalRoute) {
        throw new Error(`Original route ${originalRouteId} not found in draft routes`);
      }

      if (!targetRoute) {
        throw new Error(`Target route ${newRouteId} not found in draft routes`);
      }

      try {
        // Store original state for rollback
        const originalStationOrderIndex = originalRoute.draftVersion.routeDetail.path.lstRouteStopIds.findIndex(
          (el) => el === originalStationId,
        );

        if (originalStationOrderIndex === -1) {
          throw new Error(`Station ${originalStationId} not found in original route path`);
        }

        const station = originalRoute.stations.find((el) => el.id === originalStationId);
        if (!station) {
          throw new Error(`Station ${originalStationId} not found in original route stations`);
        }

        const stationIndex = originalRoute.stations.findIndex((el) => el.id === originalStationId);

        // Step 1: Remove station from original route's lstRouteStopIds
        originalRoute.draftVersion.routeDetail.path.lstRouteStopIds.splice(originalStationOrderIndex, 1);

        // Step 2: Add station to target route's lstRouteStopIds
        targetRoute.draftVersion.routeDetail.path.lstRouteStopIds.splice(
          Number(stationOrder) + 1,
          0,
          originalStationId,
        );

        // Step 3: Remove station from original route's stations array
        originalRoute.stations.splice(stationIndex, 1);

        // Step 4: Add station to target route's stations array
        targetRoute.stations.splice(Number(stationOrder) + 1, 0, {
          ...station,
          routeId: newRouteId,
        });

        // Step 5: Handle personnel assignments using utility function
        const { pathPersonnelStations } = originalRoute.draftVersion.routeDetail;

        if (pathPersonnelStations) {
          const { sourceUpdated, targetUpdated } = transferPersonnelToStation(
            pathPersonnelStations,
            targetRoute.draftVersion.routeDetail.pathPersonnelStations,
            originalStationId,
          );

          // Update both routes' personnel assignments
          originalRoute.draftVersion.routeDetail.pathPersonnelStations = sourceUpdated;
          targetRoute.draftVersion.routeDetail.pathPersonnelStations = targetUpdated;
        }

        // Step 6: Mark both routes as modified
        commit('ADD_MODIFIED_ROUTE', originalRouteId, { root: true });
        commit('ADD_MODIFIED_ROUTE', newRouteId, { root: true });

        // Track this station as genuinely new to the target route's forward path so
        // syncStationsToReturnPath creates a return counterpart for it instead of skipping it
        // (it has no entry in stationIdMappings since its id didn't change).
        commit('TRACK_NEW_FORWARD_STATION_ID', { routeId: newRouteId, stationId: originalStationId }, { root: true });

        return { success: true };
      } catch (error) {
        console.error('Failed to assign station to route:', error);
        throw error;
      }
    },

    /**
     * Enable drawing mode for polygon selection
     */
    enableDrawingMode({ commit }) {
      commit('SET_DRAWING_MODE', true);
      commit('CLEAR_DRAWN_POLYGONS');
    },

    /**
     * Disable drawing mode
     */
    disableDrawingMode({ commit }) {
      commit('SET_DRAWING_MODE', false);
    },

    /**
     * Process polygon selection for optimization
     *
     * @param {Object} payload - Selection payload
     * @param {Array} payload.path - Polygon path coordinates
     * @param {Array} payload.visiblePeopleMarkers - Personnel markers to check
     * @param {Object} payload.google - Google Maps API instance
     */
    processPolygonSelection({ commit }, { path, visiblePeopleMarkers, google }) {
      if (!google || !google.maps || !google.maps.geometry) {
        console.error('Google Maps API not available');
        commit('SET_DRAWING_MODE', false);
        return { success: false, error: 'Google Maps API not available' };
      }

      // Create polygon overlay for containment check
      const polygon = new google.maps.Polygon({ paths: path });

      const selectedRoutes = new Set();
      const selectedUnassigned = [];

      visiblePeopleMarkers.forEach((person) => {
        const { homeLocation } = person;
        if (!homeLocation?.latitude || !homeLocation?.longitude) return;

        const latLng = new google.maps.LatLng(homeLocation.latitude, homeLocation.longitude);
        const isInPolygon = google.maps.geometry.poly.containsLocation(latLng, polygon);

        if (isInPolygon) {
          if (person.routeId) {
            selectedRoutes.add(person.routeId);
          } else if (person.id) {
            selectedUnassigned.push(person.id);
          }
        }
      });

      // Add selected routes to optimization list
      selectedRoutes.forEach((routeId) => {
        commit('ADD_TO_OPT_LIST', { list: 'optSelectedRoutes', id: routeId }, { root: true });
      });

      // Add selected unassigned personnel to optimization list
      selectedUnassigned.forEach((personnelId) => {
        commit('ADD_TO_OPT_LIST', { list: 'optSelectedUnassigned', id: personnelId }, { root: true });
      });

      // Store the drawn polygon
      commit('ADD_DRAWN_POLYGON', path);

      // Disable drawing mode after selection
      commit('SET_DRAWING_MODE', false);

      return {
        success: true,
        selectedRoutes: Array.from(selectedRoutes),
        selectedUnassigned,
      };
    },

    /**
     * Open station panel for add/edit/assign
     */
    openStationPanel({ commit }, { mode, stationData, routeId }) {
      commit('SET_SIDE_PANEL_STATE', {
        isVisible: true,
        mode,
        stationData,
        routeId,
      });
    },

    /**
     * Close station panel
     */
    closeStationPanel({ commit }) {
      commit('RESET_SIDE_PANEL_STATE');
    },

    /**
     * Clone forward-direction stations to the return path for a set of modified draft routes.
     * Creates new stations via the addStation API and updates returnPath.lstRouteStopIds.
     * Used when the return path is empty (first-time setup) — every forward station gets a
     * brand-new return station. For routes with an existing return path, use
     * syncStationsToReturnPath instead, which preserves unchanged return stations.
     *
     * @param {Array<number>} draftIds - Route IDs to clone stations for
     * @returns {Object} Map of routeId → { syncedStations, returnStopIds }
     */
    async cloneStationsToReturnPath({ rootState, commit }, draftIds) {
      const results = {};

      /* eslint-disable no-await-in-loop */
      for (let d = 0; d < draftIds.length; d += 1) {
        const routeId = draftIds[d];
        const draft = rootState.routeList.draftRoutes[routeId];
        const forwardStations = getReversedForwardStations(draft);

        if (forwardStations.length) {
          // Every forward station gets a brand-new return station — model this as a list of
          // 'create' tasks so we can reuse the same run/assemble/finalize pipeline as
          // syncRouteReturnPath (which also handles 'reuse'/'update' tasks).
          const tasks = forwardStations.map((forwardStation) => ({ type: 'create', forwardStation }));

          const taskResults = await runStationSyncTasks(tasks, routeId);
          const assembled = assembleStationSyncResults(tasks, taskResults);
          results[routeId] = finalizeStationSync(commit, routeId, assembled);
        }
      }
      /* eslint-enable no-await-in-loop */

      return results;
    },

    /**
     * Sync forward-direction station changes to the return path for modified draft routes.
     *
     * Unlike cloneStationsToReturnPath (which rebuilds the entire return path from scratch),
     * this action preserves unchanged return stations — keeping their ids, locations, timing
     * data, and personnel assignments intact. Only stations whose forward counter was
     * moved/added/deleted are affected:
     *
     * - Moved forward station → its counter return station's location is updated via addStation
     * - Added forward station → a new counter return station is created
     * - Deleted forward station → its counter return station is dropped from the return path
     * - Unchanged forward station → existing counter return station is reused as-is (no API call)
     *
     * The return path order is rebuilt to match the forward direction reversed (same as the
     * clone action), but the station ids for unchanged stops are preserved.
     *
     * Falls back to cloneStationsToReturnPath for routes with no existing return path.
     *
     * @param {Array<number>} draftIds - Route IDs to sync stations for
     * @returns {Object} Map of routeId → { syncedStations, returnStopIds, reusedCount }
     */
    async syncStationsToReturnPath({ rootState, commit, dispatch }, draftIds) {
      const results = {};
      const routesNeedingFullClone = [];

      /* eslint-disable no-await-in-loop */
      for (let d = 0; d < draftIds.length; d += 1) {
        const routeId = draftIds[d];
        const draft = rootState.routeList.draftRoutes[routeId];
        const routeDetail = draft?.draftVersion?.routeDetail;
        const forwardStopIds = routeDetail?.path?.lstRouteStopIds || [];
        const existingReturnStopIds = routeDetail?.returnPath?.lstRouteStopIds || [];

        if (forwardStopIds.length) {
          // No existing return path → fall back to full clone for this route
          if (!existingReturnStopIds.length) {
            routesNeedingFullClone.push(routeId);
          } else {
            const stationMap = buildStationMap(draft);
            const forwardStations = forwardStopIds.map((id) => stationMap.get(id)).filter(Boolean);

            if (forwardStations.length) {
              // eslint-disable-next-line no-await-in-loop
              const result = await syncRouteReturnPath({
                routeId,
                forwardStations,
                existingReturnStopIds,
                previousReturnMap: routeDetail?.returnPathPersonnelStations || {},
                riderLegEdits: rootState.routeList.modifiedRiderLegs?.[routeId] || {},
                stationMap,
                stationIdMappings: rootState.routeList.stationIdMappings?.[routeId] || {},
                newForwardStationIds: rootState.routeList.newForwardStationIds?.[routeId] || {},
                commit,
              });

              results[routeId] = result;
            }
          }
        } else if (existingReturnStopIds.length) {
          // Forward direction is now empty (its last stop was moved/removed) but return
          // stations still remain - prune any that were mirrors of the now-gone forward
          // stop(s) instead of leaving them stranded (see pruneOrphanedReturnStations).
          const stationMap = buildStationMap(draft);
          const result = pruneOrphanedReturnStations({ routeId, existingReturnStopIds, stationMap, commit });
          if (result) {
            results[routeId] = result;
          }
        }
      }

      // Handle routes that need a full clone (empty return path)
      if (routesNeedingFullClone.length) {
        const cloneResults = await dispatch('cloneStationsToReturnPath', routesNeedingFullClone);
        Object.assign(results, cloneResults);
      }
      /* eslint-enable no-await-in-loop */

      return results;
    },

    /**
     * Transform draft route data for API submission.
     * Deduplicates stations per leg and rebuilds pathPersonnelStations.
     */
    prepareDraftData({ rootState }, draftIds) {
      if (!draftIds?.length) return undefined;
      const routeDrafts = {};

      draftIds.forEach((id) => {
        const rawDraft = rootState.routeList.draftRoutes[id];

        routeDrafts[id] = { ...rawDraft };
        routeDrafts[id].draftVersion = cloneObject(routeDrafts[id].draftVersion);

        const stationsRaw = (routeDrafts[id].stations || []).filter((s) => s.routeId);
        const { routeDetail } = routeDrafts[id].draftVersion || {};

        const { isStub: isReturnPathStub, stopIds: returnLegStopIds } = getReturnLegInfo(routeDetail);

        const firstLegStationIds = new Set(routeDetail?.path?.lstRouteStopIds || []);
        const returnLegStationIds = new Set(returnLegStopIds);

        if (isReturnPathStub) {
          delete routeDrafts[id].draftVersion.routeDetail.returnPath;
          delete routeDrafts[id].draftVersion.routeDetail.returnPathPersonnelStations;
        }

        const firstLegMap = new Map();
        const returnLegMap = new Map();

        stationsRaw.forEach((s) => {
          // Skip null/undefined stations or stations without valid IDs
          if (!s || !s.id) return;

          if (firstLegStationIds.has(s.id)) {
            const existing = firstLegMap.get(s.id);
            if (!existing || (s.expectedArrivalHour != null && existing.expectedArrivalHour == null)) {
              firstLegMap.set(s.id, s);
            }
          }
          if (returnLegStationIds.has(s.id)) {
            const existing = returnLegMap.get(s.id);
            if (!existing || (s.expectedArrivalHour != null && existing.expectedArrivalHour == null)) {
              returnLegMap.set(s.id, s);
            }
          }
        });

        const stations = [...firstLegMap.values(), ...returnLegMap.values()];
        routeDrafts[id].stations = stations;

        // A stop on both legs is a single station object whose personnelIds/personnels carry no
        // leg, so deriving either map from it would copy riders across legs (the backend's
        // optimizer import and station merge both produce such stops). For those, the per-leg
        // maps — kept current by ASSIGN_PERSONNEL_TO_STOP_DRAFT and the return-path sync — are
        // left as they are.
        const isOnBothLegs = (s) => firstLegStationIds.has(s.id) && returnLegStationIds.has(s.id);

        // Build pathPersonnelStations from FORWARD leg stations only
        const existingPersonnelStations = routeDetail?.pathPersonnelStations || {};
        const updatedPersonnelStations = { ...existingPersonnelStations };

        const forwardPersonnel = [...firstLegMap.values()]
          .filter((s) => !isOnBothLegs(s))
          .map((s) => {
            if (s.personnelIds) {
              return s.personnelIds.map((pid) => ({ [pid]: s.id })).flat();
            }
            return s?.personnels?.map((p) => ({ [getPersonnelId(p)]: s.id })).flat();
          })
          .flat();

        forwardPersonnel.forEach((p) => {
          if (p) {
            const key = Object.keys(p)[0];
            if (key && key !== 'undefined') {
              updatedPersonnelStations[key] = p[key];
            }
          }
        });

        routeDrafts[id].draftVersion.routeDetail.pathPersonnelStations = updatedPersonnelStations;

        // Build returnPathPersonnelStations from RETURN leg stations only
        if (!isReturnPathStub && returnLegMap.size > 0) {
          const existingReturnPersonnelStations = routeDetail?.returnPathPersonnelStations || {};
          const updatedReturnPersonnelStations = { ...existingReturnPersonnelStations };

          const returnPersonnel = [...returnLegMap.values()]
            .filter((s) => !isOnBothLegs(s))
            .map((s) => {
              if (s.personnelIds) {
                return s.personnelIds.map((pid) => ({ [pid]: s.id })).flat();
              }
              return s?.personnels
                ?.map((p) => {
                  const personnelId = getPersonnelId(p);
                  return personnelId != null ? { [personnelId]: s.id } : null;
                })
                .filter(Boolean)
                .flat();
            })
            .flat();

          returnPersonnel.forEach((p) => {
            if (p) {
              const key = Object.keys(p)[0];
              if (key && key !== 'undefined') {
                updatedReturnPersonnelStations[key] = p[key];
              }
            }
          });

          routeDrafts[id].draftVersion.routeDetail.returnPathPersonnelStations = updatedReturnPersonnelStations;
        }
      });

      return routeDrafts;
    },

    /**
     * Save station timing stats for a set of prepared draft routes
     */
    async saveStationStats(_ctx, routeDrafts) {
      if (!routeDrafts) return;

      const routeStations = {};

      Object.keys(routeDrafts).forEach((routeId) => {
        const draft = routeDrafts[routeId];
        if (!draft) return;

        const draftVersionId = draft.draftVersion?.id;
        if (!draftVersionId) return;

        const { stations } = draft;
        const { routeDetail } = draft.draftVersion;
        if (!stations || !Array.isArray(stations)) return;

        const firstLegStationIds = routeDetail?.path?.lstRouteStopIds || [];
        const returnLegStationIds = routeDetail?.returnPath?.lstRouteStopIds || [];
        const firstLegIdSet = new Set(firstLegStationIds);
        const returnLegIdSet = new Set(returnLegStationIds);

        const firstLegMap = new Map();
        const returnLegMap = new Map();

        stations.forEach((s) => {
          if (firstLegIdSet.has(s.id)) {
            const existing = firstLegMap.get(s.id);
            if (!existing || (s.expectedArrivalHour && !existing.expectedArrivalHour)) {
              firstLegMap.set(s.id, s);
            }
          }
          if (returnLegIdSet.has(s.id)) {
            const existing = returnLegMap.get(s.id);
            if (!existing || (s.expectedArrivalHour && !existing.expectedArrivalHour)) {
              returnLegMap.set(s.id, s);
            }
          }
        });

        const firstLegStationsPayload = firstLegStationIds
          .map((stationId) => {
            const station = firstLegMap.get(stationId);
            if (!station) return null;
            return {
              expectedArrivalHour: station.expectedArrivalHour,
              expectedDepartureHour: station.expectedDepartureHour,
              stationId: station.id,
              templateRouteDirection: 'FIRST_LEG',
            };
          })
          .filter(Boolean);

        const returnLegStationsPayload = returnLegStationIds
          .map((stationId) => {
            const station = returnLegMap.get(stationId);
            if (!station) return null;
            return {
              expectedArrivalHour: station.expectedArrivalHour,
              expectedDepartureHour: station.expectedDepartureHour,
              stationId: station.id,
              templateRouteDirection: 'RETURN_LEG',
            };
          })
          .filter(Boolean);

        routeStations[draftVersionId] = [...firstLegStationsPayload, ...returnLegStationsPayload];
      });

      if (Object.keys(routeStations).length > 0) {
        await routeListService.updateStationStats(routeStations);
      }
    },

    /**
     * Save draft routes to the backend and update store state.
     * Returns the updated routes on success or throws on error.
     */
    async saveDrafts(context, { instanceId, draftIds }) {
      return persistDraftRoutes(context, { instanceId, draftIds, activate: false });
    },

    /**
     * Fetch route data from backend and update store.
     */
    async fetchRouteData({ commit, rootState, state }, { instanceId, selectedDate: overrideDate } = {}) {
      const selectedDate = overrideDate ?? state.selectedDate;
      commit('SET_IS_LOADING', true);
      try {
        const result = await routeListService.getRouteData(instanceId, selectedDate);
        commit('SET_ALL_ROUTES', result, { root: true });

        const ringId = rootState.routeList.currentWorkgroup?.template?.ringId;
        if (ringId) {
          const [route] = rootState.routeList.allRoutes;
          commit('SET_IS_CLOSE_EDIT_MESSAGE', !route?.serviceCompanyId);
        } else {
          commit('SET_IS_CLOSE_EDIT_MESSAGE', false);
        }
      } catch (error) {
        console.error('Failed to fetch route data:', error);
        throw error;
      } finally {
        commit('SET_IS_LOADING', false);
      }
    },

    /**
     * Activate draft routes and update store state.
     * Includes return path snapshot preservation for round-trip routes.
     * Calls fetchRouteData after activation to refresh the route list.
     */
    async activateDrafts(context, { instanceId, draftIds }) {
      return persistDraftRoutes(context, { instanceId, draftIds, activate: true });
    },

    /**
     * Assign personnel to a stop/station
     *
     * @param {Object} payload - Assignment payload
     * @param {Number|String} payload.stationId - Station ID
     * @param {Number|String} payload.personnelId - Personnel ID
     * @param {Number|String} payload.routeId - Target route ID
     * @param {Number|String} payload.currentRouteId - Current route ID (if personnel already assigned)
     * @param {Object} payload.stationInfo - Station information object
     * @param {Object} payload.personnelInfo - Personnel information object
     */
    async assignPersonnelToStop({ commit, rootState }, payload) {
      const { stationId, personnelId, routeId, currentRouteId, stationInfo } = payload;

      // Validate required fields
      if (!stationId || !routeId || !personnelId) {
        throw new Error('Missing required data for personnel assignment');
      }

      // Store original state for rollback
      const draftRoute = rootState.routeList.draftRoutes[routeId];
      const currentDraftRoute = currentRouteId ? rootState.routeList.draftRoutes[currentRouteId] : null;

      const snapshotLegMaps = (draft) => ({
        pathPersonnelStations: cloneObject(draft?.draftVersion?.routeDetail?.pathPersonnelStations || {}),
        returnPathPersonnelStations: cloneObject(draft?.draftVersion?.routeDetail?.returnPathPersonnelStations || {}),
      });

      const originalState = {
        targetLegMaps: snapshotLegMaps(draftRoute),
        currentLegMaps: snapshotLegMaps(currentDraftRoute),
        personnelStationData: rootState.routePersonnel?.personnelStations?.[personnelId]
          ? cloneObject(rootState.routePersonnel.personnelStations[personnelId])
          : null,
      };

      // Record the assignment on the leg the clicked stop belongs to. Mirroring it onto the
      // opposite leg is deliberately NOT done here: that happens in syncStationsToReturnPath,
      // which only runs once the user confirms applying changes to both directions.
      const direction = resolveClickedLegDirection(draftRoute, stationId);
      const isCrossRouteMove = currentRouteId != null && String(currentRouteId) !== String(routeId);

      try {
        // Step 1: Assign personnel to stop in draft route
        commit(
          'ASSIGN_PERSONNEL_TO_STOP_DRAFT',
          {
            stationId,
            personnelId,
            routeId,
            currentRouteId,
            direction,
          },
          { root: true },
        );

        // Prepare station data for personnel assignment
        const station = {
          id: stationId,
          name: stationInfo?.name || stationInfo?.description,
          description: stationInfo?.description || stationInfo?.name,
        };

        // Step 2: Update personnel station assignment in routePersonnel module
        commit(
          'routePersonnel/UPDATE_PERSONNEL_STATION_ASSIGNMENT',
          { personnelId, station, routeId, direction },
          { root: true },
        );

        // A cross-route move leaves the rider's opposite-leg stop on the old route, which
        // ASSIGN_PERSONNEL_TO_STOP_DRAFT already cleared from routeList; clear it here too
        if (isCrossRouteMove) {
          commit(
            'routePersonnel/UPDATE_PERSONNEL_STATION_ASSIGNMENT',
            { personnelId, station: null, direction: direction === 'TO' ? 'FROM' : 'TO' },
            { root: true },
          );
        }

        return { success: true };
      } catch (error) {
        console.error('Failed to assign personnel to stop:', error);

        // Rollback: Restore original state with deep cloning to ensure proper restoration
        try {
          // Rollback target route personnel stations
          if (draftRoute?.draftVersion?.routeDetail) {
            Object.assign(draftRoute.draftVersion.routeDetail, cloneObject(originalState.targetLegMaps));
          }

          // Rollback current route personnel stations if different route
          if (currentRouteId && currentDraftRoute?.draftVersion?.routeDetail) {
            Object.assign(currentDraftRoute.draftVersion.routeDetail, cloneObject(originalState.currentLegMaps));
          }

          // Rollback personnel station data
          if (rootState.routePersonnel?.personnelStations && originalState.personnelStationData) {
            rootState.routePersonnel.personnelStations[personnelId] = cloneObject(originalState.personnelStationData);
          }
        } catch (rollbackError) {
          console.error('Failed to rollback personnel assignment:', rollbackError);
        }

        throw error;
      }
    },
  },
};
